import type { UnresolvedRecord } from '../interfaces'
import type { RootState } from '../redux/store'
import flatTexts from './flatTexts'

/**
 * The copy template cinnamon ships when a storefront does not override it
 * (COM-1397). Matches the Spanish string in the COM-1395 mock. A storefront
 * override to `""` falls back to this baseline rather than hiding — the
 * widget's render guard watches `points`, not `message`, so emptying the
 * copy here does not drop the row. A storefront that wants no pill omits
 * the component entry from its SCS body instead.
 */
export const DEFAULT_LOYALTY_POINTS_EARNED_MESSAGE =
  '¡Vas a ganar {{points}} puntos con esta compra!'

/**
 * The two `LoyaltyPointsEarned` props cinnamon owns rather than the config
 * author (COM-1397).
 *
 * `points` is the Redux slice value `useLoyaltyPointsPreflight` writes. The
 * config author cannot bind it as a `{{variable}}` because the preflight is
 * authored here, not in the config DSL.
 *
 * `message` is a joint responsibility: the config author owns the override
 * through `config.texts.cart.loyaltyPointsEarned` (same seam as every other
 * cart label), and cinnamon owns the baseline when that key is empty. The
 * widget substitutes `{{points}}` into the template itself for a direct
 * mount, but cinnamon pre-substitutes here before returning the prop — see
 * below.
 *
 * ⚠️ **`{{points}}` is resolved here, not left for the widget.** The
 * injector feeds its return value through `useCinnamonProps.resolveProps`,
 * which runs `processData`. `processData` treats a whole-string token like
 * `"{{points}}"` (a storefront that wants only the number) as a global
 * context lookup. `points` is not a global key, so the whole string becomes
 * `undefined`, and the widget crashes on `undefined.replace(...)` for a
 * positive points value. (A token embedded in a longer string takes a
 * different `processData` branch and survives.) Pre-substituting here lets
 * both templates — bare and sentence — reach the widget as plain strings.
 *
 * The widget's own `.replace('{{points}}', ...)` becomes a no-op in
 * cinnamon's path, which is fine: a positive `points` always arrives
 * with its token already filled in. On `null`/`0` the widget hides before
 * touching `message`, so leaving the token literal in the slice-is-empty
 * branch is harmless.
 */
export const getLoyaltyPointsEarnedProps = (
  state: RootState,
  /*
   * Second parameter so the signature matches the sibling injectors in
   * `PANEL_PROP_INJECTORS`. Unused here — the config author does not
   * supply either prop — but kept for the Map's type uniformity.
   */
  _props: UnresolvedRecord = {},
): UnresolvedRecord => {
  const points = state.defaultSlice.loyaltyPointsToEarn
  const authoredMessage = flatTexts(state.defaultSlice.config?.texts?.cart)
    .loyaltyPointsEarned
  const template =
    typeof authoredMessage === 'string' && authoredMessage.length > 0
      ? authoredMessage
      : DEFAULT_LOYALTY_POINTS_EARNED_MESSAGE
  /*
   * Only substitute when there is a number to substitute in. On `null`/`0`
   * the widget's own render guard hides the pill before it reads
   * `message`, so leaving the token literal keeps the slice-is-empty
   * branch inert and side-steps the whole-string `processData` trap in a
   * harmless direction.
   */
  const message =
    typeof points === 'number' && points > 0
      ? template.replace(/\{\{points\}\}/g, String(points))
      : template
  return {
    points,
    message,
  }
}

export default getLoyaltyPointsEarnedProps
